// Domain 07 CLI: file systems (flash, nvram, TFTP), IOS images and boot,
// configuration management (copy / erase / configure replace / reload), CDP and LLDP,
// and the ROM monitor. Behaviour derives from device state (see net/system.js).
import {
  sysOf, flashFile, flashUsed, flashFree, flashName, bootProblem, md5Of, tftpHost, tftpPath, configFile, cdpOn, lldpOn, IMAGES,
} from '../net/system.js';
import { roundTrip, dev, ifStatus, isHost, linkOf, peerOf } from '../net/sim.js';
import { logEvent } from '../net/services.js';
import { cdpIf, shortIf, ifSort } from './ifnames.js';
import { runningConfig } from './show.js';

const INVALID = ["% Invalid input detected at '^' marker."];
const pad = (s, n) => String(s).padEnd(n);
const lpad = (s, n) => String(s).padStart(n);
const noSys = (s) => !sysOf(s.d);
/** Configuration text without the "Building configuration" header (what a file holds). */
export const configText = (net, id) => runningConfig(net, id).map((l) => (typeof l === 'string' ? l : l.text)).filter((l, i) => !(i < 3 && /^(Building configuration|Current configuration|$)/.test(l)));
const bodyOf = (lines) => (lines || []).filter((l) => !/^(Building configuration|Current configuration|Using \d+ out of)/.test(l) && l !== '');
/** Has the running configuration changed since it was saved? */
export const unsaved = (net, id) => bodyOf(configText(net, id)).join('\n') !== bodyOf(net.devices[id].startup).join('\n');

// ------------------------------------------------------------ flash listing
function flashList(d, style) {
  const s = d.sys;
  const fs = d.kind === 'switch' ? 'flash' : 'flash0';
  if (style === 'dir') {
    return [`Directory of ${fs}:/`, '', ...s.flash.files.map((f, i) => `${lpad(i + 1, 5)}  -rw-  ${lpad(f.size, 11)}  ${f.date}  ${f.name}`), '', `${s.flash.size} bytes total (${flashFree(d)} bytes free)`];
  }
  return ['-#- --length-- -----date/time------ path', ...s.flash.files.map((f, i) => `${pad(i + 1, 4)}${lpad(f.size, 10)} ${f.date} ${f.name}`), '', `${flashFree(d)} bytes available (${flashUsed(d)} bytes used)`];
}

// ------------------------------------------------------------ question chains
/** steps: [{ prompt, def? }] → done(answers) */
function askChain(steps, done) {
  const answers = [];
  const next = (i) => ({
    prompt: steps[i].prompt(answers),
    answer: (raw) => {
      const v = raw.trim() || (typeof steps[i].def === 'function' ? steps[i].def(answers) : steps[i].def) || '';
      answers.push(v);
      return i + 1 < steps.length ? { lines: [], ask: next(i + 1) } : done(answers);
    },
  });
  return { lines: [], ask: next(0) };
}
const hostStep = { prompt: () => 'Address or name of remote host []? ' };
const defName = (s) => `${s.d.hostname.toLowerCase()}-confg`;

/** Reach the TFTP server: { ok, host, err } */
function tftpReach(s, ip) {
  const rt = roundTrip(s.net, s.id, ip, { proto: 'udp', port: 69 });
  const host = rt.ok ? tftpHost(s.net, ip) : null;
  if (!rt.ok || !host || host.tftp.enabled === false) return { ok: false };
  const via = rt.fwd?.path?.[0]?.out;
  return { ok: true, host, via };
}
const bangs = (size) => '!'.repeat(Math.max(2, Math.min(40, Math.round(size / 3e6))));
const secs = (size) => Math.max(0.1, size / 2.9e6).toFixed(3);
const tftpErr = (ip, file, why) => [{ text: `%Error opening tftp://${ip}/${file} (${why})`, delay: why === 'Timed out' ? 2500 : 300 }];

function toTftp(s, getLines, kindLabel) {
  return askChain([hostStep, { prompt: (a) => `Destination filename [${defName(s)}]? `, def: () => defName(s) }], ([ip, file]) => {
    const r = tftpReach(s, ip);
    if (!r.ok) return { lines: tftpErr(ip, file, 'Timed out'), ok: false };
    const lines = getLines();
    const f = configFile(file, lines, r.host.tftp.stamp || 'Oct 14 2026 08:02:41 +08:00');
    r.host.tftp.files[file] = { ...f, from: s.d.hostname, what: kindLabel };
    r.host.tftp.log.push({ op: 'WRQ', from: s.d.hostname, file, size: f.size });
    return { lines: ['!!', `${f.size} bytes copied in ${secs(f.size * 300)} secs (${Math.round(f.size / 0.4)} bytes/sec)`] };
  });
}
/** Copy a TFTP file into running-config (merge), startup-config (replace) or flash. */
function fromTftp(s, target) {
  const steps = [hostStep, { prompt: () => 'Source filename []? ' }];
  if (target === 'flash') steps.push({ prompt: (a) => `Destination filename [${a[1]}]? `, def: (a) => a[1] });
  else steps.push({ prompt: () => `Destination filename [${target}-config]? `, def: () => `${target}-config` });
  return askChain(steps, ([ip, file, dst]) => {
    const head = [`Accessing tftp://${ip}/${file}...`];
    const r = tftpReach(s, ip);
    if (!r.ok) return { lines: [...head, ...tftpErr(ip, file, 'Timed out')], ok: false };
    const f = r.host.tftp.files[file];
    if (!f) return { lines: [...head, ...tftpErr(ip, file, 'No such file or directory')], ok: false };
    r.host.tftp.log.push({ op: 'RRQ', from: s.d.hostname, file, size: f.size });
    const load = [`Loading ${file} from ${ip} (via ${r.via || 'GigabitEthernet0/0'}): ${bangs(f.size)}`];
    if (target === 'flash') {
      if (flashFile(s.d, dst)) return { lines: [`%Error copying tftp://${ip}/${file} (File exists — delete it first or choose another name)`], ok: false };
      if (f.size > flashFree(s.d)) return { lines: [...head, `%Error copying tftp://${ip}/${file} (Not enough space on device)`], ok: false };
      s.d.sys.flash.files.push({ ...f, name: dst, date: 'Oct 14 2026 08:20:03 +08:00' });
      return { lines: [...head, ...load, `[OK - ${f.size} bytes]`, '', `${f.size} bytes copied in ${secs(f.size)} secs (${Math.round(f.size / secs(f.size))} bytes/sec)`] };
    }
    if (f.kind !== 'config') return { lines: [...head, `%Error: ${file} is not a configuration file`], ok: false };
    if (target === 'startup') {
      s.d.startup = ['Building configuration...', '', `Current configuration : ${f.size} bytes`, ...f.lines.filter((l) => !/^(Building configuration|Current configuration)/.test(l) && l !== '')];
      s.d.startupState = null;
      return { lines: [...head, ...load, `[OK - ${f.size} bytes]`, '', '[OK]', `${f.size} bytes copied in ${secs(f.size * 300)} secs`] };
    }
    const errs = s.replayLines(f.lines);
    return { lines: [...head, ...load, `[OK - ${f.size} bytes]`, '', ...errs, `${f.size} bytes copied in ${secs(f.size * 300)} secs (${Math.round(f.size / 0.4)} bytes/sec)`] };
  });
}

// ------------------------------------------------------------ CDP / LLDP
function neighbours(net, id, proto) {
  const d = dev(net, id);
  const out = [];
  for (const n of Object.keys(d.interfaces).sort(ifSort)) {
    const l = linkOf(net, id, n);
    if (!l || !ifStatus(net, id, n).connected) continue;
    const p = peerOf(l, id, n);
    const pd = dev(net, p.dev);
    if (proto === 'cdp') {
      if (!cdpOn(d, n) || !pd.cdpCapable && isHost(pd)) continue;
      if (!isHost(pd) && !cdpOn(pd, p.ifc)) continue;
      if (isHost(pd) && !pd.cdp) continue;
    } else {
      if (!lldpOn(d, n, 'rx')) continue;
      if (isHost(pd) ? !pd.lldp : !lldpOn(pd, p.ifc, 'tx')) continue;
    }
    out.push({ local: n, pd, pifc: p.ifc });
  }
  return out;
}
const mgmtIp = (pd, pifc) => {
  if (isHost(pd)) return pd.ipcfg?.ip || null;
  const i = pd.interfaces[pifc];
  if (i?.ip?.ip) return i.ip.ip;
  const svi = Object.entries(pd.interfaces).find(([k, x]) => /^Vlan/.test(k) && x.ip?.ip);
  return svi ? svi[1].ip.ip : Object.values(pd.interfaces).find((x) => x.ip?.ip)?.ip.ip || null;
};
const platformText = (pd) => pd.platform || (pd.kind === 'router' ? 'cisco CISCO2911/K9' : pd.kind === 'switch' ? 'cisco WS-C2960-24TT-L' : 'unknown');
const capOf = (pd) => pd.capability || (pd.kind === 'router' ? 'R' : pd.kind === 'switch' ? 'S' : 'H');
const verText = (pd) => (pd.sys?.image ? `Cisco IOS Software, ${pd.kind === 'switch' ? 'C2960 Software (C2960-LANBASEK9-M)' : 'C2900 Software (C2900-UNIVERSALK9-M)'}, Version ${pd.sys.image.version}, RELEASE SOFTWARE` : pd.swVersion || 'unknown');
function cdpBrief(net, id) {
  return [
    'Capability Codes: R - Router, T - Trans Bridge, B - Source Route Bridge',
    '                  S - Switch, H - Host, I - IGMP, r - Repeater, P - Phone',
    '', 'Device ID        Local Intrfce     Holdtme    Capability  Platform  Port ID',
    ...neighbours(net, id, 'cdp').map(({ local, pd, pifc }) => `${pad(pd.hostname, 17)}${pad(cdpIf(local), 18)}${pad('163', 11)}${pad(capOf(pd), 12)}${pad(platformText(pd).split(' ').pop().slice(0, 9), 10)}${isHost(pd) ? pifc : cdpIf(pifc)}`),
    '', `Total cdp entries displayed : ${neighbours(net, id, 'cdp').length}`,
  ];
}
function cdpDetail(net, id) {
  const out = [];
  for (const { local, pd, pifc } of neighbours(net, id, 'cdp')) {
    const ip = mgmtIp(pd, pifc);
    out.push('-------------------------', `Device ID: ${pd.hostname}`, 'Entry address(es): ', ...(ip ? [`  IP address: ${ip}`] : []),
      `Platform: ${platformText(pd)},  Capabilities: ${{ R: 'Router Source-Route-Bridge', S: 'Switch IGMP', H: 'Host' }[capOf(pd)] || capOf(pd)}`,
      `Interface: ${local},  Port ID (outgoing port): ${pifc}`, 'Holdtime : 163 sec', '', 'Version :', verText(pd), '', 'advertisement version: 2', ...(ip ? ['Management address(es): ', `  IP address: ${ip}`] : []), '');
  }
  out.push(`Total cdp entries displayed : ${neighbours(net, id, 'cdp').length}`);
  return out;
}
function lldpBrief(net, id) {
  const d = dev(net, id);
  if (!d.sys?.lldp) return ['% LLDP is not enabled'];
  const n = neighbours(net, id, 'lldp');
  return ['Capability codes:', '    (R) Router, (B) Bridge, (T) Telephone, (C) DOCSIS Cable Device', '    (W) WLAN Access Point, (P) Repeater, (S) Station, (O) Other', '',
    'Device ID           Local Intf     Hold-time  Capability      Port ID',
    ...n.map(({ local, pd, pifc }) => `${pad(pd.hostname, 20)}${pad(shortIf(local), 15)}${pad('120', 11)}${pad(pd.lldpCap || (pd.kind === 'router' ? 'R' : pd.kind === 'switch' ? 'B' : 'S'), 16)}${isHost(pd) ? pifc : shortIf(pifc)}`),
    '', `Total entries displayed: ${n.length}`];
}
function lldpDetail(net, id) {
  const d = dev(net, id);
  if (!d.sys?.lldp) return ['% LLDP is not enabled'];
  const out = [];
  for (const { local, pd, pifc } of neighbours(net, id, 'lldp')) {
    const ip = mgmtIp(pd, pifc);
    out.push('------------------------------------------------', `Local Intf: ${shortIf(local)}`, `Chassis id: ${(pd.baseMac || pd.nic?.mac || '000000000000').match(/..../g).join('.')}`, `Port id: ${isHost(pd) ? pifc : shortIf(pifc)}`,
      `Port Description: ${(isHost(pd) ? pd.portDesc : pd.interfaces[pifc]?.description) || pifc}`, `System Name: ${pd.hostname}`, '', 'System Description: ', isHost(pd) ? (pd.lldpDesc || pd.os) : verText(pd), '',
      'Time remaining: 104 seconds', `System Capabilities: ${pd.lldpCap || (pd.kind === 'router' ? 'R' : 'B')}`, `Enabled Capabilities: ${pd.lldpCap || (pd.kind === 'router' ? 'R' : 'B')}`,
      'Management Addresses:', ...(ip ? [`    IP: ${ip}`] : ['    (none)']), '');
  }
  out.push(`Total entries displayed: ${neighbours(net, id, 'lldp').length}`);
  return out;
}

// ------------------------------------------------------------ specs
const S = (spec) => ({ ...spec, run: (s, a) => (noSys(s) ? INVALID : spec.run(s, a)) });
export const MGMT_PRIV = [
  ...['show flash', 'show flash:', 'show flash0:'].map((p) => S({ pattern: p, run: (s) => flashList(s.d, 'show') })),
  ...['dir', 'dir flash:', 'dir flash0:'].map((p) => S({ pattern: p, run: (s) => flashList(s.d, 'dir') })),
  S({ pattern: 'show cdp neighbors detail', run: (s) => cdpDetail(s.net, s.id) }),
  S({ pattern: 'show cdp', run: (s) => (s.d.sys.cdp === false ? ['% CDP is not enabled'] : ['Global CDP information:', '        Sending CDP packets every 60 seconds', '        Sending a holdtime value of 180 seconds', '        Sending CDPv2 advertisements is  enabled']) }),
  S({ pattern: 'show lldp', run: (s) => (s.d.sys.lldp ? ['', 'Global LLDP Information:', '    Status: ACTIVE', '    LLDP advertisements are sent every 30 seconds', '    LLDP hold time advertised is 120 seconds', '    LLDP interface reinitialisation delay is 2 seconds'] : ['% LLDP is not enabled']) }),
  S({ pattern: 'show lldp neighbors', run: (s) => lldpBrief(s.net, s.id) }),
  S({ pattern: 'show lldp neighbors detail', run: (s) => lldpDetail(s.net, s.id) }),
  ...['copy running-config tftp:', 'copy running-config tftp'].map((p) => S({ pattern: p, run: (s) => toTftp(s, () => configText(s.net, s.id), 'running-config') })),
  ...['copy startup-config tftp:', 'copy startup-config tftp'].map((p) => S({ pattern: p, run: (s) => (s.d.startup ? toTftp(s, () => s.d.startup, 'startup-config') : ['%Error opening nvram:/startup-config (No such file or directory)']) })),
  ...['copy tftp: running-config', 'copy tftp running-config'].map((p) => S({ pattern: p, run: (s) => fromTftp(s, 'running') })),
  ...['copy tftp: startup-config', 'copy tftp startup-config'].map((p) => S({ pattern: p, run: (s) => fromTftp(s, 'startup') })),
  ...['copy tftp: flash:', 'copy tftp flash', 'copy tftp: flash0:'].map((p) => S({ pattern: p, run: (s) => fromTftp(s, 'flash') })),
  ...['copy flash: tftp:', 'copy flash tftp'].map((p) => S({ pattern: p, run: (s) => askChain([{ prompt: () => 'Source filename []? ' }, hostStep, { prompt: (a) => `Destination filename [${a[0]}]? `, def: (a) => a[0] }], ([src, ip, dst]) => {
    const f = flashFile(s.d, flashName(src));
    if (!f) return { lines: [`%Error opening flash:${src} (File not found)`], ok: false };
    const r = tftpReach(s, ip);
    if (!r.ok) return { lines: tftpErr(ip, dst, 'Timed out'), ok: false };
    r.host.tftp.files[dst] = { ...f, name: dst, from: s.d.hostname };
    r.host.tftp.log.push({ op: 'WRQ', from: s.d.hostname, file: dst, size: f.size });
    return { lines: [bangs(f.size), `${f.size} bytes copied in ${secs(f.size)} secs (${Math.round(f.size / secs(f.size))} bytes/sec)`] };
  }) })),
  ...['copy running-config flash:', 'copy startup-config flash:'].map((p) => S({ pattern: p, run: (s) => askChain([{ prompt: () => `Destination filename [${p.includes('running') ? 'running-config' : 'startup-config'}]? `, def: () => (p.includes('running') ? 'running-config' : 'startup-config') }], ([dst]) => {
    const lines = p.includes('running') ? configText(s.net, s.id) : s.d.startup;
    if (!lines) return { lines: ['%Error opening nvram:/startup-config (No such file or directory)'], ok: false };
    const name = flashName(dst);
    s.d.sys.flash.files = s.d.sys.flash.files.filter((f) => f.name !== name);
    const f = configFile(name, lines.filter((l) => !/^(Building configuration|Current configuration)/.test(l)), 'Oct 14 2026 08:05:12 +08:00');
    s.d.sys.flash.files.push(f);
    return { lines: [`${f.size} bytes copied in 0.204 secs (${Math.round(f.size / 0.204)} bytes/sec)`] };
  }) })),
  ...['copy flash: running-config', 'copy flash: startup-config'].map((p) => S({ pattern: p, run: (s) => askChain([{ prompt: () => 'Source filename []? ' }, { prompt: () => `Destination filename [${p.split(' ')[2]}]? `, def: () => p.split(' ')[2] }], ([src]) => {
    const f = flashFile(s.d, flashName(src));
    if (!f) return { lines: [`%Error opening flash:${src} (File not found)`], ok: false };
    if (f.kind !== 'config') return { lines: [`%Error: flash:${f.name} is not a configuration file`], ok: false };
    if (p.endsWith('startup-config')) { s.d.startup = ['Building configuration...', '', `Current configuration : ${f.size} bytes`, ...f.lines.filter((l) => l !== '')]; s.d.startupState = null; return { lines: [`${f.size} bytes copied in 0.188 secs`] }; }
    return { lines: [...s.replayLines(f.lines), `${f.size} bytes copied in 0.188 secs`] };
  }) })),
  S({ pattern: 'copy startup-config running-config', run: (s) => (s.d.startup ? askChain([{ prompt: () => 'Destination filename [running-config]? ', def: () => 'running-config' }], () => {
    const errs = s.replayLines(s.d.startup);
    const size = s.d.startup.join('\n').length;
    return { lines: [...errs, `${size} bytes copied in 0.188 secs (${Math.round(size / 0.188)} bytes/sec)`] };
  }) : ['%Error opening nvram:/startup-config (No such file or directory)']) }),
  S({ pattern: 'configure replace <FLASHFILE>', run: (s, [p]) => {
    const f = flashFile(s.d, flashName(p));
    if (!f) return [`%Error opening ${p} (File not found)`];
    if (f.kind !== 'config') return [`%Error: ${p} is not a configuration file`];
    return { lines: [], ask: { prompt: 'This will apply all necessary additions and deletions\nto replace the current running configuration with the\ncontents of the specified configuration file, which is\nassumed to be a complete configuration, not a partial\nconfiguration. Enter Y if you are sure you want to proceed. ? [no]: ', answer: (ans) => {
      if (!/^y/i.test(ans.trim())) return { lines: ['% Configuration replace aborted'], ok: false };
      s.replaceConfig(f.lines);
      logEvent(s.net, s.id, `%PARSER-6-CONFIGREPLACE: Configuration replaced with ${p}`);
      return { lines: ['Total number of passes: 1', 'Rollback Done', '', `%PARSER-6-CONFIGREPLACE: Configuration replaced with ${p}`] };
    } } };
  } }),
  S({ pattern: 'more <FLASHFILE>', run: (s, [p]) => {
    const f = flashFile(s.d, flashName(p));
    if (!f) return [`%Error opening ${p} (File not found)`];
    return f.kind === 'config' ? f.lines.flatMap((l) => String(l).split('\n')) : ['%Error: binary file (IOS image) — not displayed'];
  } }),
  S({ pattern: 'verify /md5 <FLASHFILE>', run: (s, [p]) => {
    const f = flashFile(s.d, flashName(p));
    if (!f) return [`%Error opening ${p} (File not found)`];
    return [`${'.'.repeat(Math.min(60, Math.round(f.size / 2e6)))}Done!`, `verify /md5 (${p}) = ${f.md5}`];
  } }),
  S({ pattern: 'verify /md5 <FLASHFILE> <WORD>', run: (s, [p, h]) => {
    const f = flashFile(s.d, flashName(p));
    if (!f) return [`%Error opening ${p} (File not found)`];
    const dots = `${'.'.repeat(Math.min(60, Math.round(f.size / 2e6)))}Done!`;
    return f.md5 === h.toLowerCase() ? [dots, `Verified (${p}) = ${f.md5}`] : [dots, '%Error verifying ' + p, `Computed signature  = ${f.md5}`, `Submitted signature = ${h.toLowerCase()}`];
  } }),
  S({ pattern: 'delete <FLASHFILE>', run: (s, [p]) => askChain([{ prompt: () => `Delete filename [${flashName(p)}]? `, def: () => flashName(p) }, { prompt: (a) => `Delete flash:/${a[0]}? [confirm]` }], ([name, ok]) => {
    if (/^n/i.test(ok)) return { lines: [] };
    const f = flashFile(s.d, flashName(name));
    if (!f) return { lines: [`%Error deleting flash:/${name} (File not found)`], ok: false };
    s.d.sys.flash.files = s.d.sys.flash.files.filter((x) => x !== f);
    return { lines: [] };
  }) }),
  ...['erase startup-config', 'write erase', 'erase nvram:'].map((p) => S({ pattern: p, run: (s) => ({ lines: [], ask: { prompt: 'Erasing the nvram filesystem will remove all configuration files! Continue? [confirm]', answer: (a) => {
    if (/^n/i.test(a.trim())) return { lines: [] };
    s.d.startup = null; s.d.startupState = null;
    logEvent(s.net, s.id, '%SYS-7-NV_BLOCK_INIT: Initialized the geometry of nvram');
    return { lines: ['[OK]', 'Erase of nvram: complete', '%SYS-7-NV_BLOCK_INIT: Initialized the geometry of nvram'] };
  } } }) })),
  S({ pattern: 'reload', run: (s) => {
    const confirm = { prompt: 'Proceed with reload? [confirm]', answer: (a) => (/^n/i.test(a.trim()) ? { lines: [] } : { lines: s.reloadDevice() }) };
    if (!unsaved(s.net, s.id)) return { lines: [], ask: confirm };
    return { lines: [], ask: { prompt: 'System configuration has been modified. Save? [yes/no]: ', answer: (a) => {
      const v = a.trim().toLowerCase();
      if (!/^(y|ye|yes|n|no)$/.test(v)) return { lines: ['% Please answer \'yes\' or \'no\'.'], ask: { prompt: 'System configuration has been modified. Save? [yes/no]: ', answer: (b) => (/^y/i.test(b.trim()) ? { lines: [...s.saveConfig().map((x) => x), ''], ask: confirm } : { lines: [], ask: confirm }) } };
      return v.startsWith('y') ? { lines: s.saveConfig(), ask: confirm } : { lines: [], ask: confirm };
    } } };
  } }),
];

const isHex = (t) => /^0x[0-9a-f]{1,4}$/i.test(t);
export const MGMT_CONFIG = [
  // lines that appear in a saved configuration and are accepted as no-ops when it is replayed
  ...['version <WORD>', 'boot-start-marker', 'boot-end-marker', 'ip classless', 'ip flow-export version 9', 'spanning-tree extend system-id', 'aaa session-id common', 'line aux 0',
    'no service timestamps debug datetime msec', 'service timestamps debug datetime msec', 'no service timestamps log datetime msec', 'ip cef', 'no ipv6 cef']
    .map((p) => ({ pattern: p, run: (s) => { if (p === 'no service timestamps log datetime msec') s.d.timestamps = false; return []; } })),
  { pattern: 'service timestamps log datetime msec', kinds: ['switch'], run: (s) => { s.d.timestamps = true; return []; } },
  { pattern: 'logging buffered <WORD>', run: (s) => { (s.d.logging ||= { hosts: [] }).buffered = true; return []; } },
  S({ pattern: 'boot system <FLASHFILE>', run: (s, [p]) => { const v = `flash:${flashName(p)}`; if (!s.d.sys.boot.includes(v)) s.d.sys.boot.push(v); return []; } }),
  S({ pattern: 'boot system flash <WORD>', run: (s, [p]) => { const v = `flash:${p}`; if (!s.d.sys.boot.includes(v)) s.d.sys.boot.push(v); return []; } }),
  S({ pattern: 'no boot system', run: (s) => { s.d.sys.boot = []; return []; } }),
  S({ pattern: 'no boot system <FLASHFILE>', run: (s, [p]) => { s.d.sys.boot = s.d.sys.boot.filter((b) => flashName(b) !== flashName(p)); return []; } }),
  S({ pattern: 'config-register <WORD>', run: (s, [v]) => {
    if (!isHex(v) || s.d.kind !== 'router') return INVALID;
    const reg = `0x${v.slice(2).toUpperCase().padStart(4, '0')}`;
    s.d.sys.confregNext = reg === s.d.sys.confreg ? null : reg;
    return [];
  } }),
  S({ pattern: 'cdp run', run: (s) => { s.d.sys.cdp = true; return []; } }),
  S({ pattern: 'no cdp run', run: (s) => { s.d.sys.cdp = false; return []; } }),
  S({ pattern: 'lldp run', run: (s) => { s.d.sys.lldp = true; return []; } }),
  S({ pattern: 'no lldp run', run: (s) => { s.d.sys.lldp = false; return []; } }),
];
export const MGMT_IF = [
  S({ pattern: 'cdp enable', run: (s) => s.eachIf((i) => { delete i.cdp; }) }),
  S({ pattern: 'no cdp enable', run: (s) => s.eachIf((i) => { i.cdp = false; }) }),
  S({ pattern: 'lldp transmit', run: (s) => s.eachIf((i) => { delete i.lldpTx; }) }),
  S({ pattern: 'no lldp transmit', run: (s) => s.eachIf((i) => { i.lldpTx = false; }) }),
  S({ pattern: 'lldp receive', run: (s) => s.eachIf((i) => { delete i.lldpRx; }) }),
  S({ pattern: 'no lldp receive', run: (s) => s.eachIf((i) => { i.lldpRx = false; }) }),
];

// ------------------------------------------------------------ ROM monitor
export const ROMMON = [
  { pattern: 'dir flash:', run: (s) => ['         File size           Checksum   File name', ...s.d.sys.flash.files.map((f) => `${lpad(f.size, 10)} bytes (0x${f.size.toString(16)})   0x${f.md5.slice(0, 4)}    ${f.name}`)] },
  { pattern: 'dir flash0:', run: (s) => ['         File size           Checksum   File name', ...s.d.sys.flash.files.map((f) => `${lpad(f.size, 10)} bytes (0x${f.size.toString(16)})   0x${f.md5.slice(0, 4)}    ${f.name}`)] },
  { pattern: 'confreg <WORD>', run: (s, [v]) => {
    if (!isHex(v)) return ['Invalid argument'];
    s.d.sys.confreg = `0x${v.slice(2).toUpperCase().padStart(4, '0')}`; s.d.sys.confregNext = null;
    return ['', 'You must reset or power cycle for new config to take effect'];
  } },
  { pattern: 'confreg', run: (s) => [`Configuration register = ${s.d.sys.confreg}`] },
  { pattern: 'reset', run: (s) => s.reloadDevice() },
  { pattern: 'boot', run: (s) => s.reloadDevice() },
  { pattern: 'boot <FLASHFILE>', run: (s, [p]) => {
    const f = flashFile(s.d, flashName(p));
    const why = bootProblem(s.d, f);
    if (why) return [why === 'not-found' ? `boot: cannot open "${p}"` : `boot: cannot load "${p}" — ${why === 'platform' ? 'not a valid image for this platform' : 'image checksum error'}`];
    return s.reloadDevice({ file: f, once: true });
  } },
  { pattern: 'help', run: () => ['boot               boot up an external process', 'confreg            configuration register utility', 'dir                list files in file system', 'reset              system reset'] },
];

// ------------------------------------------------------------ running-config lines
export function mgmtGlobalLines(d) {
  const s = d.sys;
  if (!s) return [];
  const out = [];
  if (d.kind === 'router') out.push('boot-start-marker', ...s.boot.map((b) => `boot system ${b}`), 'boot-end-marker', '!');
  else if (s.boot.length) out.push(...s.boot.map((b) => `boot system ${b}`), '!');
  return out;
}
export function mgmtTailLines(d) {
  const s = d.sys;
  if (!s) return [];
  const out = [];
  if (s.cdp === false) out.push('no cdp run');
  if (s.lldp) out.push('lldp run');
  // The configuration register is kept outside the configuration (show version reports it).
  if (out.length) out.push('!');
  return out;
}
export function mgmtIfLines(d, n, i) {
  const out = [];
  if (i.cdp === false) out.push(' no cdp enable');
  if (i.lldpTx === false) out.push(' no lldp transmit');
  if (i.lldpRx === false) out.push(' no lldp receive');
  return out;
}
export { IMAGES, md5Of, tftpPath };
